import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Artikel, StammdatenService } from '@app/services/stammdaten.service';
import { CommonModule } from '@angular/common';
import { environment } from '@env/environment';

@Component({
  selector: 'app-dialog-artikel',
  templateUrl: './dialog-artikel.component.html',
  styleUrls: ['./dialog-artikel.component.css'],
})
export class ArtikelDialogComponent implements OnInit {
  artikel: Artikel;
  frmArtikel: UntypedFormGroup;

  // Stammdaten-sourced dropdowns (basiseinheit, ausbringung)
  basiseinheiten: any[] = [];
  ausbringungen: any[] = [];

  // Environment-sourced dropdowns
  systemkomponentenOptions = environment.artikel_systemkomponenten;
  statusOptions = environment.artikel_status_options;
  biozidProduktartenOptions = environment.artikel_biozid_produktarten;

  loading = true;
  isSubmitting = false;

  constructor(
    public dialogRef: MatDialogRef<ArtikelDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _fb: UntypedFormBuilder,
    private stammdatenService: StammdatenService,
    private snackBar: MatSnackBar
  ) {
    this.artikel = data?.artikel || {
      id: 0,
      beschreibung: '',
      art: '',
      basiseinheit: '',
      ausbringung: '',
      sortierung: 0,
      systemkomponente: '',
      zulassungs_nummer: '',
      wirkstoff: '',
      status_id: 1,
      biozid_produktart: ''
    };

    this.frmArtikel = this._fb.group({
      id: [0],
      beschreibung: ['', Validators.required],
      art: ['Artikel'],
      basiseinheit: [''],
      ausbringung: [''],
      sortierung: [0],
      systemkomponente: [''],
      zulassungs_nummer: [''],
      wirkstoff: [''],
      status_id: [1],
      biozid_produktart: ['']
    });
  }

  ngOnInit() {
    this.loadStammdaten();
  }

  loadStammdaten() {
    this.stammdatenService.getArtikelStammdaten().subscribe(
      (response: any) => {
        if (response) {
          this.basiseinheiten = response.artikel_basiseinheiten || [];
          this.ausbringungen = response.artikel_ausbringungen || [];
        }
        this.loading = false;
        this.patchForm();
      },
      () => {
        this.basiseinheiten = [];
        this.ausbringungen = [];
        this.loading = false;
        this.patchForm();
      }
    );
  }

  private patchForm() {
    this.frmArtikel.patchValue({
      id: this.artikel.id,
      beschreibung: this.artikel.beschreibung,
      art: 'Artikel',
      basiseinheit: this.artikel.basiseinheit,
      ausbringung: this.artikel.ausbringung,
      sortierung: this.artikel.sortierung ?? 0,
      systemkomponente: this.artikel.systemkomponente,
      zulassungs_nummer: this.artikel.zulassungs_nummer,
      wirkstoff: this.artikel.wirkstoff,
      status_id: this.artikel.status_id ?? 1,
      biozid_produktart: this.artikel.biozid_produktart
    });
  }

  onConfirm(): void {
    if (this.frmArtikel.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const raw = this.frmArtikel.value;

      const payload: Partial<Artikel> = {
        ...(this.data.mode !== 'create' && { nummer: this.artikel.nummer }),
        beschreibung: raw.beschreibung || '',
        art: 'Artikel',
        basiseinheit: raw.basiseinheit || '',
        ausbringung: raw.ausbringung || '',
        sortierung: raw.sortierung != null ? Number(raw.sortierung) : 0,
        systemkomponente: raw.systemkomponente || '',
        zulassungs_nummer: raw.zulassungs_nummer || '',
        wirkstoff: raw.wirkstoff || '',
        status_id: raw.status_id != null ? Number(raw.status_id) : 1,
        biozid_produktart: raw.biozid_produktart || '',
        beschreibung_2: '',
        code: '',
        beschreibung_code: '',
        in_code: '',
        alternative_beschreibung: '',
        belegtext: '',
        zulassungen: '',
        sicherheitsdatenblatt: ''
      };

      if (this.data.mode === 'create') {
        this.stammdatenService.createArtikel(payload).subscribe({
          next: (response) => {
            this.snackBar.open('Produkt erfolgreich erstellt', 'Schließen', { duration: 3000 });
            this.dialogRef.close(response);
          },
          error: () => {
            this.snackBar.open('Fehler beim Erstellen des Produkts', 'Schließen', { duration: 3000 });
            this.isSubmitting = false;
          }
        });
      } else {
        this.stammdatenService.updateArtikel(raw.id, payload).subscribe({
          next: (response) => {
            this.snackBar.open('Produkt erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
            this.dialogRef.close(response);
          },
          error: () => {
            this.snackBar.open('Fehler beim Aktualisieren des Produkts', 'Schließen', { duration: 3000 });
            this.isSubmitting = false;
          }
        });
      }
    }
  }

  onDismiss(): void {
    this.dialogRef.close(false);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
